"use client";

import { LANDING_CONTAINER_CLASS } from "@/components/landing/constants";
import { Check, Clock, Palette, Sparkles, Wand2 } from "lucide-react";
import SmallFeatureCard from "@/components/landing/small-feature-card";
import PdfShowcase from "@/components/landing/features/pdf-showcase";
import DishDatabaseShowcase from "@/components/landing/features/dish-database-showcase";
import EmailNewsletterShowcase from "@/components/landing/features/email-newsletter-showcase";
import { Field } from "@restomat/components/client/ui";
import { Input } from "@restomat/components/ui";
import { Label } from "@restomat/components/client/ui";
import { Button } from "@restomat/components/client/ui";
import { type ReactNode, useRef } from "react";
import { cn } from "@restomat/shared-utils/utils";
import { useTranslations } from "next-intl";
import { motion, useInView } from "framer-motion";
import { fadeInUp, staggerContainer } from "./animations";

type Feature = {
  cardKey: string;
  content: ReactNode;
  preparing?: boolean;
};

function AiTextShowcase() {
  const t = useTranslations("landing.automation.cards.aiTextHelper");
  return (
    <div className={"flex flex-col items-end gap-2"}>
      <Field className={"w-full"}>
        <Label htmlFor={"ai-showcase"}>{t("eventDescriptionLabel")}</Label>
        <Input
          id={"ai-showcase"}
          placeholder={t("eventDescriptionPlaceholder")}
        />
      </Field>
      <Button variant={"default"} size={"xs"}>
        <Sparkles size={18} />
        {t("improve")}
      </Button>
    </div>
  );
}

function BackgroundShowcase() {
  const t = useTranslations("landing.automation.cards.background");
  return (
    <div className={"flex flex-col gap-3"}>
      <div
        className={
          "flex items-center gap-3 border-2 border-foreground rounded-md px-3 py-2 bg-card"
        }
      >
        <Clock size={20} className={"text-primary shrink-0"} />
        <div className={"flex flex-col"}>
          <span className={"text-sm font-semibold"}>{t("creating")}</span>
          <span className={"text-xs text-tertiary"}>{t("creatingStep")}</span>
        </div>
      </div>
      <div
        className={
          "flex items-center gap-3 border-2 border-foreground rounded-md px-3 py-2 bg-secondary"
        }
      >
        <Check size={20} className={"shrink-0"} />
        <span className={"text-sm font-semibold"}>{t("done")}</span>
      </div>
    </div>
  );
}

const features: Feature[] = [
  {
    cardKey: "pdfMenu",
    content: <PdfShowcase className={"w-full h-full"} />,
  },
  {
    cardKey: "aiTextHelper",
    content: <AiTextShowcase />,
  },
  {
    cardKey: "smartPhotoEdits",
    content: <SmartPhotoShowcase />,
  },
  {
    cardKey: "brandKit",
    content: <BrandKitShowcase />,
  },
  {
    cardKey: "background",
    content: <BackgroundShowcase />,
  },
  {
    cardKey: "dishDatabase",
    content: <DishDatabaseShowcase className={"w-full h-full"} />,
  },
  {
    cardKey: "publishCalendar",
    content: <PublishCalendarShowcase />,
  },
  {
    cardKey: "emailNewsletter",
    content: <EmailNewsletterShowcase className={"w-full h-full"} />,
    preparing: true,
  },
];

function SmartPhotoShowcase() {
  const t = useTranslations("landing.automation.smartPhoto");
  const suggestions = [
    { label: t("balanceLight"), selected: true },
    { label: t("enhanceColor"), selected: true },
    { label: t("smoothBackground"), selected: true },
    { label: t("removeReflection"), selected: false },
  ];
  return (
    <div
      className={
        "flex flex-col gap-3 border-2 border-foreground rounded-md px-3 py-3 bg-card text-sm"
      }
    >
      <div className={"flex items-center gap-2"}>
        <Wand2 size={18} className={"text-primary shrink-0"} />
        <span className={"font-semibold"}>{t("analyzed")}</span>
      </div>
      <ul className={"flex flex-col gap-1.5"}>
        {suggestions.map((s) => (
          <li
            key={s.label}
            className={cn(
              "flex items-center gap-2 rounded-md border-2 border-foreground px-2 py-1.5",
              s.selected ? "bg-secondary" : "bg-background",
            )}
          >
            <span
              className={cn(
                "flex h-4 w-4 items-center justify-center rounded border-2 border-foreground shrink-0",
                s.selected ? "bg-foreground text-background" : "bg-background",
              )}
              aria-hidden
            >
              {s.selected ? <Check size={12} strokeWidth={3} /> : null}
            </span>
            <span className={cn(!s.selected && "text-tertiary line-through")}>
              {s.label}
            </span>
          </li>
        ))}
      </ul>
      <Button variant={"default"} size={"xs"} className={"self-end"}>
        <Sparkles size={16} />
        {t("improve")}
      </Button>
    </div>
  );
}

function BrandKitShowcase() {
  const t = useTranslations("landing.automation.brandKit");
  return (
    <div
      className={
        "flex flex-col gap-3 border-2 border-foreground rounded-md px-4 py-3 bg-card text-sm md:text-base"
      }
    >
      <div className={"flex items-center gap-3"}>
        <div
          className={
            "flex h-9 w-9 items-center justify-center rounded-full bg-foreground text-background font-black"
          }
          aria-hidden
        >
          R
        </div>
        <div className={"flex flex-col leading-tight"}>
          <span className={"font-semibold"}>{t("establishment")}</span>
          <span className={"text-xs text-tertiary"}>{t("label")}</span>
        </div>
      </div>
      <div className={"flex items-center gap-2"}>
        <span className={"flex items-center gap-1"}>
          <Palette size={14} className={"text-tertiary"} />
          <span className={"text-xs uppercase tracking-wider text-tertiary"}>
            {t("colors")}
          </span>
        </span>
        <span
          className={
            "h-5 w-5 rounded-full border-2 border-foreground bg-primary"
          }
          aria-hidden
        />
        <span
          className={
            "h-5 w-5 rounded-full border-2 border-foreground bg-secondary"
          }
          aria-hidden
        />
        <span
          className={
            "h-5 w-5 rounded-full border-2 border-foreground bg-accent"
          }
          aria-hidden
        />
        <span
          className={
            "h-5 w-5 rounded-full border-2 border-foreground bg-foreground"
          }
          aria-hidden
        />
      </div>
      <div className={"flex items-center justify-between gap-2 text-xs"}>
        <span className={"text-tertiary uppercase tracking-wider"}>{t("font")}</span>
        <span className={"font-primary text-base"}>BEBAS NEUE</span>
      </div>
    </div>
  );
}

function PublishCalendarShowcase() {
  const t = useTranslations("landing.automation.calendar");
  const days = [
    { label: t("days.mon"), dot: "bg-secondary" },
    { label: t("days.tue"), dot: null },
    { label: t("days.wed"), dot: "bg-primary" },
    { label: t("days.thu"), dot: null },
    { label: t("days.fri"), dot: "bg-accent" },
    { label: t("days.sat"), dot: "bg-primary" },
    { label: t("days.sun"), dot: null },
  ];
  return (
    <div
      className={
        "flex flex-col gap-3 border-2 border-foreground rounded-md px-3 py-3 bg-card text-sm"
      }
    >
      <div className={"flex items-center justify-between"}>
        <span className={"font-semibold"}>{t("week")}</span>
        <span className={"text-xs text-tertiary"}>{t("postsCount", { count: 4 })}</span>
      </div>
      <div className={"grid grid-cols-7 gap-1.5"}>
        {days.map((d) => (
          <div
            key={d.label}
            className={
              "flex flex-col items-center gap-1 rounded-md border-2 border-foreground bg-background py-2"
            }
          >
            <span className={"text-[10px] font-semibold uppercase tracking-wider"}>
              {d.label}
            </span>
            <span
              className={cn(
                "h-2 w-2 rounded-full",
                d.dot ?? "bg-transparent",
              )}
              aria-hidden
            />
          </div>
        ))}
      </div>
      <div className={"text-xs text-tertiary"}>{t("schedule")}</div>
    </div>
  );
}

export default function AutomationSection() {
  const t = useTranslations("landing.automation");
  const headerRef = useRef(null);
  const headerInView = useInView(headerRef, { once: true, amount: "some" });

  return (
    <section
      id="automation"
      className="border-b-4 border-black bg-background pt-20 pb-16"
    >
      <div className={`${LANDING_CONTAINER_CLASS} space-y-8 md:space-y-16`}>
        <motion.header
          ref={headerRef}
          initial="hidden"
          animate={headerInView ? "visible" : "hidden"}
          variants={staggerContainer}
        >
          <motion.h2 className={"leading-10 md:leading-24"} variants={fadeInUp}>
            {t("heading")}
          </motion.h2>
          <motion.div
            className={"font-medium text-2xl 2xl:text-5xl"}
            variants={fadeInUp}
          >
            {t("subheading")}
          </motion.div>
        </motion.header>

        <div
          className={cn(
            "flex flex-col gap-8 md:gap-12",
            "md:block md:columns-2 md:gap-8 md:space-y-8",
            "xl:columns-3 xl:gap-16 xl:space-y-12",
          )}
        >
          {features.map((feat, index) => (
            <SmallFeatureCard
              title={t(`cards.${feat.cardKey}.title`)}
              description={t(`cards.${feat.cardKey}.description`)}
              content={feat.content}
              preparing={feat.preparing}
              key={`feature-${index}`}
            />
          ))}
        </div>
      </div>
    </section>
  );
}
